<script setup lang="ts">
import { computed, defineProps, type PropType, ref, toRef } from 'vue'
import type { CustomRockGrading } from '@/stores/type/customRockGrading.type'
import { useCustomRockGradingStore } from '@/stores/customRockGrading'
import { storeToRefs } from 'pinia'
import { ColourPicker, TextField } from '@haskoning/vue-component-library'

const props = defineProps({
  item: {
    type: Object as PropType<CustomRockGrading>,
    required: true
  },
  isCheckboxShown: {
    type: Boolean,
    default: false
  }
})

const item = toRef(props, 'item')

const customRockGradingStore = useCustomRockGradingStore()
const { selectedItems } = storeToRefs(customRockGradingStore)
const isSelected = computed<boolean>({
  get: () => selectedItems.value?.includes(item.value) || false,
  set: () => {
    const index = selectedItems.value.findIndex(
      (selectedItem: CustomRockGrading) => selectedItem.id === item.value?.id
    )

    index !== -1 ? selectedItems.value.splice(index, 1) : selectedItems.value.push(item.value)
  }
})

const updateColour = (colour: string) => {
  if (item.value) {
    item.value.colour = colour
    customRockGradingStore.update(item.value)
  }
}

const editableFields = ['name', 'NLL_kg', 'NUL_kg', 'yNLL', 'yNUL']
const isEditing = ref<boolean>(false)
const toggleEdit = () => {
  isEditing.value = !isEditing.value
}
</script>

<template>
  <tr>
    <td v-if="isCheckboxShown">
      <v-checkbox v-model="isSelected" density="compact" hide-details />
    </td>
    <td class="text-center">{{ item.id }}</td>

    <template v-if="!isEditing">
      <td>{{ item.name }}</td>
      <td class="text-center">{{ item.NLL_kg }}</td>
      <td class="text-center">{{ item.NUL_kg }}</td>
      <td class="text-center">{{ item.yNLL }}</td>
      <td class="text-center">{{ item.yNUL }}</td>
    </template>

    <template v-else>
      <td v-for="key in editableFields" :key="key" :class="key === 'name' ? 'pl-0' : ''">
        <TextField
          :field="customRockGradingStore.fields[key]"
          :modelValue="item"
          :no-help-text="true"
          density="comfortable"
          single-line
          hide-details
          @update="customRockGradingStore.update(item)"
        />
      </td>
    </template>

    <td class="text-center">
      <ColourPicker :item="item" :is-editing="isEditing" @update-colour="updateColour" />
    </td>
    <td>{{ item.fullname }}</td>
    <td v-if="customRockGradingStore.canChangeCustomRockGrading" class="text-center">
      <v-btn variant="text" :icon="isEditing ? 'mdi-close' : '$edit'" @click.stop="toggleEdit" />
    </td>
  </tr>
</template>

<style scoped></style>
